import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import propz from 'propz';
import { ParentAvailabilityOverview } from 'Src/components/ParentAvailabilityOverview/ParentAvailabilityOverview';
import { AvailabilityReportCard } from 'Src/components/AvailabilityReportCard/AvailabilityReportCard';
import { AppUser } from 'Src/views/App/App';
import { Report } from 'Src/models/report';
import { Point } from 'Src/models/venue';
import { getPoint } from 'Src/helpers/venue/venue';
import './EventAvailabilityResponses.scss';

interface Props {
  reports: Report[];
  user: AppUser;
  onViewMapClick: (point: Point) => void;
  onEditReport?: (report: Report) => void;
}

const getReportGroupKey = (report: Report, index: number) => {
  const userId =
    propz.get(report, ['playerDetails', 'userId'], '') ||
    propz.get(report, ['playerDetailsData', 'id'], '') ||
    propz.get(report, ['playerDetailsData', 'userId'], '');
  const permissionId = propz.get(report, ['playerDetails', 'permissionId'], '');
  const firstName = String(propz.get(report, ['playerDetailsData', 'firstName'], '')).trim().toLowerCase();
  const lastName = String(propz.get(report, ['playerDetailsData', 'lastName'], '')).trim().toLowerCase();
  const legacyStudentKey = firstName || lastName ? `${firstName}:${lastName}` : '';
  // A student's permission can be replaced or be absent on older messages.
  // The event history still belongs to the same student, so userId is the
  // stable grouping key and permissionId is only a legacy fallback.
  return userId
    ? `user:${userId}`
    : permissionId
    ? `permission:${permissionId}`
    : legacyStudentKey
    ? `student:${legacyStudentKey}`
    : report.id || `report-${index}`;
};

const getCreatedAtValue = (report: Report) => {
  const value = moment(report.createdAt);
  return value.isValid() ? value.valueOf() : 0;
};

export const EventAvailabilityResponses: React.FC<Props> = ({ reports, user, onViewMapClick, onEditReport }) => {
  const [expandedId, setExpandedId] = useState<string>();

  if (!reports.length) {
    return (
      <div className="eventResponsesEmpty">
        <span aria-hidden="true">A</span>
        <strong>No availability reports yet</strong>
        <p>Availability responses for this event will appear here.</p>
      </div>
    );
  }

  const openMap = (report: Report) => {
    const venuePoint = propz.get(report, ['eventData', 'venue', 'point']);
    const postcodePoint = propz.get(report, ['eventData', 'venue', 'postcodeData', 'point']);
    onViewMapClick(getPoint(venuePoint, postcodePoint));
  };

  const groupedReports = reports.reduce((result, report, index) => {
    const key = getReportGroupKey(report, index);
    if (!result[key]) result[key] = [];
    result[key].push(report);
    return result;
  }, {} as { [key: string]: Report[] });

  const reportGroups = Object.keys(groupedReports).map(key => {
    const responses = [...groupedReports[key]].sort((first, second) => getCreatedAtValue(second) - getCreatedAtValue(first));
    return { key, report: responses[0], history: responses.slice(1) };
  });

  return (
    <div className="eventResponsesGrid">
      <section className="eventAvailabilityPanel">
        <header><h3>Availability</h3><p>{reportGroups.filter(group => typeof group.report.isTakePart !== 'boolean').length ? `${reportGroups.filter(group => typeof group.report.isTakePart !== 'boolean').length} ${reportGroups.length === 1 ? 'student needs' : 'students need'} a reply` : 'All replies sent. You can change them until the event starts.'}</p></header>
        <ParentAvailabilityOverview items={reportGroups.map(group => group.report) as any} user={user} onReply={onEditReport as any} />
      </section>
      <div className="eventAvailabilityLegacy" aria-hidden="true">
      {reportGroups.map(({ key, report, history }) => {
        const isExpanded = expandedId === key;
        const eventStart = moment(propz.get(report, ['eventData', 'startTime'], ''));
        const isPending = typeof report.isTakePart !== 'boolean';
        const latestRequest = [report, ...history].find(item =>
          ['REQUEST', 'REMINDER', 'REOPEN'].includes(String((item as any).availabilityRevisionType || ''))
        );
        const responseCutoff = moment(propz.get(latestRequest, ['availabilityDeadline'], propz.get(report, ['eventData', 'startTime'], '')));
        const isExpired = isPending && responseCutoff.isValid() && moment().isSameOrAfter(responseCutoff);
        const editingEnabled = Boolean(propz.get(user, ['activeSchool', 'settings', 'availabilityResponseEditingEnabled'], true));
        const canEdit = !!onEditReport && !isExpired && (isPending || editingEnabled) && (!eventStart.isValid() || moment().isBefore(eventStart));
        const senderName = propz.get(report, ['sender', 'fullName'], 'Unknown');
        const sentByStudent =
          String(propz.get(report, ['sender', 'userId'], '')) ===
          String(propz.get(report, ['playerDetails', 'userId'], ''));
        const createdAt = moment(report.createdAt);
        const updatedAt = moment(report.updatedAt);
        const wasUpdated = updatedAt.isValid() && createdAt.isValid() && updatedAt.diff(createdAt, 'seconds') > 1;

        return (
          <article className={`eventResponseItem ${isExpanded ? 'is-expanded' : ''}`} key={key}>
            <AvailabilityReportCard
              item={report as any}
              type="EVENT"
              onViewMapClick={() => openMap(report)}
              user={user}
            />

            {history.length ? (
              <details className="eventResponseItem__historyDisclosure">
                <summary>
                  <span>
                    <strong>Response history</strong>
                    <small>{history.length} previous {history.length === 1 ? 'update' : 'updates'}</small>
                  </span>
                  <span className="eventResponseItem__historyDisclosureAction">View</span>
                </summary>
                <section className="eventResponseItem__history" aria-label="Previous availability responses">
                  <h4>Previous responses</h4>
                  <ol>
                    {history.map((previousReport, historyIndex) => {
                      const previousSender = propz.get(previousReport, ['sender', 'fullName'], 'Unknown');
                      const previousSentByStudent =
                        String(propz.get(previousReport, ['sender', 'userId'], '')) ===
                        String(propz.get(previousReport, ['playerDetails', 'userId'], ''));
                      const previousCreatedAt = moment(previousReport.createdAt);
                      const revisionType = String((previousReport as any).availabilityRevisionType || '');
                      const isRequestRevision = ['REQUEST', 'REMINDER', 'REOPEN'].includes(revisionType);
                      const currentUserId = String(propz.get(user, ['id'], propz.get(user, ['_id'], '')));
                      const previousSenderId = String(propz.get(previousReport, ['sender', 'userId'], ''));
                      const historyLabel = isRequestRevision
                        ? revisionType === 'REMINDER' ? 'Reminder received' : revisionType === 'REOPEN' ? 'Request reopened' : 'Request received'
                        : previousSenderId && previousSenderId === currentUserId
                        ? 'You responded'
                        : `${previousSentByStudent ? 'Student' : 'Parent'} responded`;

                      return (
                        <li key={previousReport.id || `${key}-history-${historyIndex}`}>
                          <span className={typeof previousReport.isTakePart !== 'boolean' ? 'is-pending' : previousReport.isTakePart ? 'is-available' : 'is-unavailable'}>
                            {historyLabel}{typeof previousReport.isTakePart === 'boolean' ? ` · ${previousReport.isTakePart ? 'Available' : 'Not available'}` : ''}
                          </span>
                          <div>
                            <strong>{previousSender}</strong>
                            <small>{previousSentByStudent ? 'Student' : 'Parent'}</small>
                          </div>
                          {previousCreatedAt.isValid() ? (
                            <time dateTime={previousCreatedAt.toISOString()}>
                              {previousCreatedAt.format('D MMM YYYY, HH:mm')}
                            </time>
                          ) : null}
                          {previousReport.details ? <p>{previousReport.details}</p> : null}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              </details>
            ) : (
              <div className="eventResponseItem__noHistory">
                <strong>Response history</strong>
                <span>No previous responses</span>
              </div>
            )}

            <div className="eventResponseItem__actions">
              <button type="button" onClick={() => setExpandedId(isExpanded ? undefined : key)} aria-expanded={isExpanded}>
                {isExpanded ? 'Hide details' : 'View details'}
                <span aria-hidden="true">{isExpanded ? '⌃' : '⌄'}</span>
              </button>
              {canEdit ? (
                <button type="button" className="is-primary" onClick={() => onEditReport(report)}>
                  {isPending ? 'Respond now' : 'Edit availability'}
                </button>
              ) : null}
            </div>

            {isExpanded ? (
              <div className="eventResponseItem__details">
                <dl>
                  <div>
                    <dt>Availability</dt>
                    <dd>
                      <span className={isPending ? 'is-pending' : report.isTakePart ? 'is-available' : 'is-unavailable'}>
                        {isExpired ? 'Expired' : isPending ? 'Response needed' : report.isTakePart ? 'Available' : 'Not available'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>{isPending ? 'Received from' : 'Responded by'}</dt>
                    <dd>{senderName} {isPending ? '(school staff)' : `(${sentByStudent ? 'student' : 'parent'})`}</dd>
                  </div>
                  {createdAt.isValid() ? (
                    <div>
                      <dt>Sent at</dt>
                      <dd>{createdAt.format('D MMM YYYY, HH:mm')}</dd>
                    </div>
                  ) : null}
                  {wasUpdated ? (
                    <div>
                      <dt>Last changed</dt>
                      <dd>{updatedAt.format('D MMM YYYY, HH:mm')}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>{isPending ? 'Request status' : 'School status'}</dt>
                    <dd>{isPending ? (isExpired ? 'Expired' : 'Received') : report.isActionPerformed ? 'Seen by school' : 'Awaiting school review'}</dd>
                  </div>
                </dl>
                <section>
                  <h4>Details</h4>
                  <p>{report.details || 'No additional details were provided.'}</p>
                </section>
                {!canEdit && isExpired ? (
                  <p className="eventResponseItem__closed">The response deadline has passed. This request is now read-only.</p>
                ) : !canEdit && eventStart.isValid() && moment().isSameOrAfter(eventStart) ? (
                  <p className="eventResponseItem__closed">This response can no longer be changed because the event has started.</p>
                ) : null}
              </div>
            ) : null}

          </article>
        );
      })}
      </div>
    </div>
  );
};

EventAvailabilityResponses.displayName = 'EventAvailabilityResponses';
